import { https } from "../http";
import store from "../store";
import { showError, showSuccess } from "../utils";
import {
  setSchedulerLoading,
  setShifts,
  setSchedulerSettings,
  setTemplates,
} from "../store/reducers/scheduler";

/* ---------------- shifts ---------------- */
const getShifts = async (params = {}) => {
  try {
    store.dispatch(setSchedulerLoading(true));
    const res = await https().get("/schedule/shifts", { params });
    store.dispatch(setShifts(res?.data?.data || []));
    store.dispatch(setSchedulerLoading(false));
    return res?.data?.data || [];
  } catch (error) {
    store.dispatch(setSchedulerLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
    return [];
  }
};

// Fetch shifts/appointments WITHOUT touching redux — used by the client and
// facility pages to show their own appointments without clobbering the
// scheduler calendar's shift state.
const getAppointments = async (params = {}) => {
  try {
    const res = await https().get("/schedule/shifts", { params });
    return res?.data?.data || [];
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return [];
  }
};

const getShift = async (id) => {
  try {
    const res = await https().get(`/schedule/shifts/${id}`);
    return res?.data?.data;
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

// Charting completion (Notes / MAR / Task) for a clock-in's visit.
const getVisitCharting = async (trackingId) => {
  try {
    const res = await https().get(`/schedule/visit-charting/${trackingId}`);
    return res?.data?.data;
  } catch (error) {
    return null;
  }
};

const createShift = async (data, callback) => {
  try {
    const res = await https().post("/schedule/shifts", data);
    showSuccess(data?.kind === "appointment" ? "Appointment created" : "Shift created");
    callback?.(res?.data?.data);
    return res?.data?.data;
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const updateShift = async (id, data, callback) => {
  try {
    const res = await https().put(`/schedule/shifts/${id}`, data);
    callback?.(res?.data?.data);
    return res?.data?.data;
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const deleteShift = async (id, scope = "single", callback) => {
  try {
    await https().delete(`/schedule/shifts/${id}`, { params: { scope } });
    showSuccess("Shift removed");
    callback?.();
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const addShiftNote = async (id, text, callback) => {
  try {
    const res = await https().post(`/schedule/shifts/${id}/notes`, { text });
    callback?.(res?.data?.data);
    return res?.data?.data;
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const updateShiftNote = async (id, noteId, text, callback) => {
  try {
    const res = await https().patch(`/schedule/shifts/${id}/notes/${noteId}`, { text });
    callback?.(res?.data?.data);
    return res?.data?.data;
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const deleteShiftNote = async (id, noteId, callback) => {
  try {
    const res = await https().delete(`/schedule/shifts/${id}/notes/${noteId}`);
    showSuccess("Note removed");
    callback?.(res?.data?.data);
    return res?.data?.data;
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

// Publish draft shifts (e.g. a copied week) — this is when staff get notified.
const publishShifts = async (shiftIds, callback) => {
  try {
    const res = await https().post("/schedule/publish", { shiftIds });
    showSuccess(`${res?.data?.data?.published || 0} shifts published. Staff have been notified.`);
    callback?.(res?.data?.data);
    return res?.data?.data;
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const copyWeek = async (fromDate, toDate, shiftIds, callback) => {
  try {
    const res = await https().post("/schedule/copy-week", { fromDate, toDate, shiftIds });
    showSuccess(`${res?.data?.data?.copied || 0} shifts copied`);
    callback?.(res?.data?.data);
    return res?.data?.data;
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};


const checkConflicts = async (data) => {
  try {
    const res = await https().post("/schedule/check-conflicts", data);
    return res?.data?.data?.warnings || [];
  } catch (error) {
    return [];
  }
};

const getAvailableStaff = async (params) => {
  try {
    const res = await https().get("/schedule/available-staff", { params });
    return res?.data?.data || [];
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return [];
  }
};

const exportSchedule = async (params = {}) => {
  try {
    const res = await https().get("/schedule/export", { params, responseType: "blob" });
    return res?.data;
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

/* ---------------- settings ---------------- */
const getSchedulerSettings = async () => {
  try {
    const res = await https().get("/schedule/settings");
    store.dispatch(setSchedulerSettings(res?.data?.data));
    return res?.data?.data;
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const updateSchedulerSettings = async (data, callback) => {
  try {
    const res = await https().put("/schedule/settings", data);
    store.dispatch(setSchedulerSettings(res?.data?.data));
    callback?.(res?.data?.data);
    return res?.data?.data;
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

/* ---------------- templates ---------------- */
const getTemplates = async (facility) => {
  try {
    const res = await https().get("/schedule/templates", { params: facility ? { facility } : {} });
    store.dispatch(setTemplates(res?.data?.data || []));
    return res?.data?.data || [];
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return [];
  }
};
const createTemplate = async (data) => {
  try {
    const res = await https().post("/schedule/templates", data);
    return res?.data?.data;
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};
const seedTemplates = async (templates) => {
  try {
    const res = await https().post("/schedule/templates/seed", { templates });
    return res?.data?.data || [];
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return [];
  }
};
const updateTemplate = async (id, data) => {
  try {
    const res = await https().put(`/schedule/templates/${id}`, data);
    return res?.data?.data;
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};
const deleteTemplate = async (id) => {
  try {
    await https().delete(`/schedule/templates/${id}`);
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

/* ---------------- schedules (weekly coverage patterns) ---------------- */
const getSchedules = async () => {
  try {
    const res = await https().get("/schedule/schedules");
    return res?.data?.data || [];
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return [];
  }
};
const createSchedule = async (data) => {
  try {
    const res = await https().post("/schedule/schedules", data);
    return res?.data?.data;
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};
const updateSchedule = async (id, data) => {
  try {
    const res = await https().put(`/schedule/schedules/${id}`, data);
    return res?.data?.data;
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};
const deleteSchedule = async (id) => {
  try {
    await https().delete(`/schedule/schedules/${id}`);
    return true;
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return false;
  }
};


/* ---------------- time-off requests ---------------- */
const getTimeOff = async (params = {}) => {
  try {
    const res = await https().get("/schedule/time-off", { params });
    return res?.data?.data || [];
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return [];
  }
};
const createTimeOff = async (data, callback) => {
  try {
    const res = await https().post("/schedule/time-off", data);
    callback?.(res?.data?.data);
    return res?.data?.data;
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};
const decideTimeOff = async (id, data, callback) => {
  try {
    const res = await https().put(`/schedule/time-off/${id}/decision`, data);
    callback?.(res?.data?.data);
    return res?.data?.data;
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};
const cancelTimeOff = async (id, callback) => {
  try {
    const res = await https().post(`/schedule/time-off/${id}/cancel`);
    callback?.(res?.data?.data);
    return res?.data?.data;
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};
const convertTimeOffOpenShifts = async (id, callback) => {
  try {
    const res = await https().post(`/schedule/time-off/${id}/open-shifts`);
    callback?.(res?.data?.data);
    return res?.data?.data;
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const schedulerService = {
  getShifts,
  getAppointments,
  getTimeOff,
  createTimeOff,
  decideTimeOff,
  cancelTimeOff,
  convertTimeOffOpenShifts,
  getShift,
  getVisitCharting,
  createShift,
  updateShift,
  deleteShift,
  addShiftNote,
  updateShiftNote,
  deleteShiftNote,
  publishShifts,
  copyWeek,
  checkConflicts,
  getAvailableStaff,
  exportSchedule,
  getSchedulerSettings,
  updateSchedulerSettings,
  getTemplates,
  createTemplate,
  seedTemplates,
  updateTemplate,
  deleteTemplate,
  getSchedules,
  createSchedule,
  updateSchedule,
  deleteSchedule,
};

export default schedulerService;
